import { type AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'sonner';
import {
  ANONYMOUS_PASSWORD,
  ANONYMOUS_USER,
  AUTHORIZATION,
  BASE_URL,
  LOCIZE_NS,
  OAUTH2_CLIENT_ID,
  OAUTH2_SECRET_ID,
} from '@avanta/shared/constants/common';
import { ROUTES } from '@avanta/shared/constants/routes';
import { COOKIES } from '@avanta/shared/constants/storage';
import { t } from '@avanta/shared/i18n';
import { useAuthStore } from '@avanta/shared/stores/auth';
import { type AuthInfo } from '@avanta/shared/types/auth';
import { type User } from '@avanta/shared/types/users';
import {
  getUserId,
  removeAllCookies,
  setCookieWithExpiry,
  setCookieWithoutExpiry,
} from '@avanta/shared/utils/storage';
import { service, type ServiceRequestConfig } from '../api';

const API_URL = `${BASE_URL}oauth2/token`;

export const oauthTokenParams = (
  username: string,
  password: string,
  magicToken?: string
): URLSearchParams => {
  if (magicToken) {
    return new URLSearchParams({
      grant_type: 'magic_link',
      magic_token: magicToken,
    });
  }
  return new URLSearchParams({
    username,
    password,
    grant_type: 'password',
  });
};

export const oauthToken = async (
  username: string,
  password: string,
  magicToken?: string
): Promise<AxiosResponse<AuthInfo> | undefined> => {
  const params = oauthTokenParams(username, password, magicToken);
  const config: ServiceRequestConfig<URLSearchParams> = {
    auth: {
      username: OAUTH2_CLIENT_ID,
      password: OAUTH2_SECRET_ID,
    },
    headers: {
      Authorization: AUTHORIZATION,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    skipGlobalErrorHandler: true,
  };
  const response = await service.post(API_URL, params, config);

  return response;
};

export const getAnonymousToken = async (): Promise<AxiosResponse<AuthInfo> | undefined> => {
  const token = await oauthToken(ANONYMOUS_USER, ANONYMOUS_PASSWORD);
  if (!token) {
    toast.error(t('Common.error', { ns: LOCIZE_NS.COMMON }));
    return;
  }
  setCookieWithExpiry(COOKIES.ANONYMOUS_TOKEN, token?.data?.access_token, token?.data.expires_in);
  return token;
};

export const loginWithPassword = async (
  username: string,
  password: string
): Promise<AxiosResponse<User>> => {
  const anonymousToken = await getAnonymousToken();
  const config: ServiceRequestConfig = {
    headers: {
      Authorization: `Bearer ${anonymousToken?.data.access_token}`,
    },
    skipGlobalErrorHandler: true,
  };
  return await service.post(
    `${BASE_URL}users/login`,
    {
      email: username,
      password,
    },
    config
  );
};

export const logout = async (redirect = true): Promise<void> => {
  //   await useChatStore.getState().disconnectAndReset();
  //TODO   queryClient.clear();
  removeAllCookies();
  useAuthStore.getState().clearAuth();
  if (redirect) {
    window.location.href = ROUTES.LOGIN;
  }
};

export const refreshAccessToken = async (refreshToken: string): Promise<string | null> => {
  try {
    const response = await service.post(
      API_URL,
      new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      }),
      {
        auth: {
          username: OAUTH2_CLIENT_ID,
          password: OAUTH2_SECRET_ID,
        },
        headers: {
          Authorization: AUTHORIZATION,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );
    if (response.data.refresh_token) {
      const userId = getUserId();
      setCookieWithoutExpiry(COOKIES.REFRESH_TOKEN, response.data.refresh_token);
      setCookieWithoutExpiry(COOKIES.USER_ID, userId);
    }
    return response.data.access_token;
  } catch {
    // If the refresh token fails, delete the cookie and redirect to the login
    Cookies.remove(COOKIES.REFRESH_TOKEN);
    logout(true);
    throw new Error(t('Common.refreshToken.error'));
  }
};

export {
  recoverPassword,
  verifyRecoveryCode,
  resendRecoveryCode,
  changePassword,
} from './recover-password';

export { changeUserPassword } from './change-password';

export { loginWithMagicLink, requestMagicLink } from './magic-link';
